import { apiClient } from './axios';
import type { CarePhase } from './flowApi';
// Tipul de limbă de DOCUMENT e duplicat în frontend (frontend nu importă
// @hepaderm/common) — sincronizat cu `DocumentLanguage` din backend.
import { isDocumentLanguage, type DocumentLanguage } from '../hooks/useLanguage';

// ============================================================================
// Limba documentului (§1.0c) — metadata trimise de backend la export
// ============================================================================

/**
 * Metadata despre limba documentului, așa cum o întoarce backendul la export.
 * `fallback === true` înseamnă că șablonul în limba cerută nu există și a fost
 * generată o versiune în română — frontendul trebuie să arate bannerul (§1.0c.6).
 */
export interface DocumentLanguageMeta {
  /** Limba rezolvată efectiv (limba reală a documentului generat). */
  resolvedLanguage?: DocumentLanguage;
  /** Limba cerută de client (`?lang=`). */
  requestedLanguage?: DocumentLanguage;
  /** true = s-a aplicat fallback-ul pe o altă limbă. */
  fallback: boolean;
  /** Motivul fallback-ului (ex. `TEMPLATE_NOT_AVAILABLE`). */
  fallbackReason?: string;
  /** Mesaj de banner gata de afișat, venit de la backend. */
  message?: string;
}

export interface DocumentDownloadResult {
  blob: Blob;
  meta: DocumentLanguageMeta;
  /**
   * Numele fișierului, luat din headerul `Content-Disposition` (dacă backendul
   * îl trimite). Altfel, UI-ul generează un nume de rezervă.
   */
  fileName?: string;
  /** Metadata opțională despre document (§1.2.6) — poate lipsi. */
  documentId?: string;
  documentVersion?: string;
}

/** Citeste un header case-insensitive, dintre mai multe nume acceptate. */
const readHeader = (headers: unknown, names: string[]): string | undefined => {
  if (!headers || typeof headers !== 'object') return undefined;
  const entries = Object.entries(headers as Record<string, unknown>);
  for (const name of names) {
    const wanted = name.toLowerCase();
    for (const [key, value] of entries) {
      if (key.toLowerCase() !== wanted) continue;
      if (value === undefined || value === null || value === '') continue;
      return String(value);
    }
  }
  return undefined;
};

const parseDocumentLanguageMeta = (headers: unknown): DocumentLanguageMeta => {
  const resolved = readHeader(headers, ['x-document-language', 'x-resolved-language']);
  const requested = readHeader(headers, ['x-requested-language', 'x-document-requested-language']);
  const fallback = readHeader(headers, ['x-language-fallback', 'x-document-fallback', 'x-fallback']);
  const reason = readHeader(headers, ['x-fallback-reason', 'x-language-fallback-reason']);
  const message = readHeader(headers, ['x-language-message', 'x-fallback-message', 'x-language-fallback-message']);

  return {
    resolvedLanguage: isDocumentLanguage(resolved) ? resolved : undefined,
    requestedLanguage: isDocumentLanguage(requested) ? requested : undefined,
    fallback: fallback === 'true' || fallback === '1',
    fallbackReason: reason,
    message,
  };
};

/**
 * Extrage numele fișierului din headerul `Content-Disposition`.
 * Suportă atât `filename="x.pdf"`, cât și forma RFC 5987 `filename*=UTF-8''x.pdf`.
 */
const parseContentDispositionFileName = (headers: unknown): string | undefined => {
  const disposition = readHeader(headers, ['content-disposition']);
  if (!disposition) return undefined;

  const extended = /filename\*\s*=\s*([^']*)'([^']*)'([^;]+)/i.exec(disposition);
  if (extended) {
    const raw = extended[3].trim().replace(/^"(.*)"$/, '$1');
    try {
      return decodeURIComponent(raw) || undefined;
    } catch {
      return raw || undefined;
    }
  }

  const plain = /filename\s*=\s*("([^"]*)"|[^;]+)/i.exec(disposition);
  if (!plain) return undefined;
  const value = (plain[2] ?? plain[1] ?? '').trim().replace(/^"(.*)"$/, '$1');
  return value || undefined;
};

/**
 * Descărcă un document medical al formularului (§1.2.6) și întoarce blob-ul
 * împreună cu metadata despre limba documentului.
 *
 * - `lang` = limba DOCUMENTULUI (RO / EN / SRB);
 * - `lesionId` = leziunea pentru care se generează documentul (opțional);
 * - numele fișierului vine din `Content-Disposition` (altfel UI-ul îl generează).
 */
const downloadMedicalDocumentFile = async (
  id: string,
  file: string,
  lang?: DocumentLanguage,
  lesionId?: string,
): Promise<DocumentDownloadResult> => {
  const params: { lang?: DocumentLanguage; lesionId?: string } = {};
  if (lang) params.lang = lang;
  if (lesionId) params.lesionId = lesionId;

  const r = await apiClient.get(`/dermato-forms/${id}/${file}`, {
    params: Object.keys(params).length > 0 ? params : undefined,
    responseType: 'blob',
  });

  return {
    blob: r.data as Blob,
    meta: parseDocumentLanguageMeta(r.headers),
    fileName: parseContentDispositionFileName(r.headers),
    documentId: readHeader(r.headers, ['x-document-id']),
    documentVersion: readHeader(r.headers, ['x-document-version']),
  };
};

// ============================================================================
// Enums (mirror @prisma/client enums from apps/dermato-service/prisma/schema.prisma)
// ============================================================================

export enum FormStatus {
  DRAFT = 'DRAFT',
  SUBMITTED = 'SUBMITTED',
  REVIEWED = 'REVIEWED',
  ARCHIVED = 'ARCHIVED',
}

export enum SkinType {
  TYPE_I = 'TYPE_I',
  TYPE_II = 'TYPE_II',
  TYPE_III = 'TYPE_III',
  TYPE_IV = 'TYPE_IV',
  TYPE_V = 'TYPE_V',
  TYPE_VI = 'TYPE_VI',
}

export enum PhotoSensitivity {
  NONE = 'NONE',
  MILD = 'MILD',
  MODERATE = 'MODERATE',
  SEVERE = 'SEVERE',
}

export enum LesionType {
  MACULE = 'MACULE',
  PAPULE = 'PAPULE',
  NODULE = 'NODULE',
  PLAQUE = 'PLAQUE',
  VESICLE = 'VESICLE',
  BULLA = 'BULLA',
  PUSTULE = 'PUSTULE',
  CYST = 'CYST',
  EROSION = 'EROSION',
  ULCER = 'ULCER',
  SCALE = 'SCALE',
  CRUST = 'CRUST',
  OTHER = 'OTHER',
}

export enum Laterality {
  LEFT = 'LEFT',
  RIGHT = 'RIGHT',
  MIDLINE = 'MIDLINE',
  BILATERAL = 'BILATERAL',
}

export enum LesionStatus {
  NEW = 'NEW',
  MONITORING = 'MONITORING',
  SUSPECT = 'SUSPECT',
  BENIGN = 'BENIGN',
  MALIGNANT = 'MALIGNANT',
  CLEAR = 'CLEAR',
}

export enum AbcdeConclusion {
  NO_SUSPICIOUS_FEATURES = 'NO_SUSPICIOUS_FEATURES',
  REQUIRES_DERMOSCOPY = 'REQUIRES_DERMOSCOPY',
  SUSPICIOUS_LESION = 'SUSPICIOUS_LESION',
}

export enum DermatoscopyResult {
  NO_SUSPICIOUS_FEATURES = 'NO_SUSPICIOUS_FEATURES',
  SUSPICIOUS_LESION = 'SUSPICIOUS_LESION',
  REQUIRES_FURTHER_EVALUATION = 'REQUIRES_FURTHER_EVALUATION',
  INCONCLUSIVE = 'INCONCLUSIVE',
}

export enum Device {
  DERM_LITE_DL5 = 'DERM_LITE_DL5',
  HEINE_DELTA_30 = 'HEINE_DELTA_30',
  DERM_LITE_HUHR_U = 'DERM_LITE_HUHR_U',
  FOTOFINDER_ATBM = 'FOTOFINDER_ATBM',
  OTHER = 'OTHER',
}

export enum AiClassification {
  BENIGN = 'BENIGN',
  RISK_LOW = 'RISK_LOW',
  RISK_MODERATE = 'RISK_MODERATE',
  RISK_HIGH = 'RISK_HIGH',
  INCONCLUSIVE = 'INCONCLUSIVE',
}

// D2.3 — verdict final screening (mirror la Prisma ScreeningConclusion)
export enum ScreeningConclusion {
  NEGATIVE = 'NEGATIVE',
  SUSPECT = 'SUSPECT',
  POSITIVE = 'POSITIVE',
  INCONCLUSIVE = 'INCONCLUSIVE',
  /** 2.9.3 — refuzul participării la program („EXCLUS DIN PROGRAM", PAS 3). */
  SCREENING_REFUZAT = 'SCREENING_REFUZAT',
  /** Finalizare fără leziuni (etapa 4 fără inventar de leziuni). */
  NO_LESIONS = 'NO_LESIONS',
}

/** Status pentru o eventuală revenire (mirror ReturnStatus din schema dermato). */
export enum ReturnStatus {
  RECONTROL_PROGRAMAT = 'RECONTROL_PROGRAMAT',
  MONITORIZARE = 'MONITORIZARE',
  FARA_REVENIRE = 'FARA_REVENIRE',
}

export enum AiMedicalConfirmation {
  CONFIRMED = 'CONFIRMED',
  NOT_CONFIRMED = 'NOT_CONFIRMED',
  INCONCLUSIVE = 'INCONCLUSIVE',
}

export enum BiopsyProcedureType {
  EXCISION = 'EXCISION',
  INCISION = 'INCISION',
  PUNCH = 'PUNCH',
  SHAVE = 'SHAVE',
  CORE_NEEDLE = 'CORE_NEEDLE',
  FINE_NEEDLE_ASPIRATION = 'FINE_NEEDLE_ASPIRATION',
  OTHER = 'OTHER',
}

export enum MarginStatus {
  FREE = 'FREE',
  CLOSE = 'CLOSE',
  INVOLVED = 'INVOLVED',
  NOT_ASSESSED = 'NOT_ASSESSED',
}

export enum HistoTumorType {
  BENIGN_NEVUS = 'BENIGN_NEVUS',
  MELANOMA_MALIGNANT = 'MELANOMA_MALIGNANT',
  BASAL_CELL_CARCINOMA = 'BASAL_CELL_CARCINOMA',
  SQUAMOUS_CELL_CARCINOMA = 'SQUAMOUS_CELL_CARCINOMA',
  DERMATOFIBROMA = 'DERMATOFIBROMA',
  KERATOACANTHOMA = 'KERATOACANTHOMA',
  ACTINIC_KERATOSIS = 'ACTINIC_KERATOSIS',
  OTHER = 'OTHER',
}

export enum AjccStage {
  STAGE_0 = 'STAGE_0',
  STAGE_I = 'STAGE_I',
  STAGE_IA = 'STAGE_IA',
  STAGE_IB = 'STAGE_IB',
  STAGE_II = 'STAGE_II',
  STAGE_IIA = 'STAGE_IIA',
  STAGE_IIB = 'STAGE_IIB',
  STAGE_IIC = 'STAGE_IIC',
  STAGE_III = 'STAGE_III',
  STAGE_IIIA = 'STAGE_IIIA',
  STAGE_IIIB = 'STAGE_IIIB',
  STAGE_IIIC = 'STAGE_IIIC',
  STAGE_IV = 'STAGE_IV',
  NOT_APPLICABLE = 'NOT_APPLICABLE',
}

export enum TreatmentIntent {
  CURATIVE = 'CURATIVE',
  PALLIATIVE = 'PALLIATIVE',
  ADJUVANT = 'ADJUVANT',
  NEOADJUVANT = 'NEOADJUVANT',
  PROPHYLACTIC = 'PROPHYLACTIC',
}

export enum TreatmentStatus {
  PLANNED = 'PLANNED',
  SCHEDULED = 'SCHEDULED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
}

export enum SurgeryProcedureType {
  EXCISION_NARROW = 'EXCISION_NARROW',
  EXCISION_WIDE = 'EXCISION_WIDE',
  MOHS_SURGERY = 'MOHS_SURGERY',
  FLAP_RECONSTRUCTION = 'FLAP_RECONSTRUCTION',
  GRAFT_RECONSTRUCTION = 'GRAFT_RECONSTRUCTION',
  SENTINEL_LYMPH_NODE_BIOPSY = 'SENTINEL_LYMPH_NODE_BIOPSY',
  LYMPHADENECTOMY = 'LYMPHADENECTOMY',
  OTHER = 'OTHER',
}

export enum PostOpComplication {
  NONE = 'NONE',
  BLEEDING = 'BLEEDING',
  INFECTION = 'INFECTION',
  WOUND_DEHISCENCE = 'WOUND_DEHISCENCE',
  NECROSIS = 'NECROSIS',
  SEROMA = 'SEROMA',
  LYMPHEDEMA = 'LYMPHEDEMA',
  NERVE_DAMAGE = 'NERVE_DAMAGE',
  OTHER = 'OTHER',
}

export enum ResidualTumor {
  NONE = 'NONE',
  MICROSCOPIC = 'MICROSCOPIC',
  MACROSCOPIC = 'MACROSCOPIC',
  UNKNOWN = 'UNKNOWN',
}

export enum ReinterventionNeeded {
  YES = 'YES',
  NO = 'NO',
}

export enum FollowUpStatus {
  FAVORABLE = 'FAVORABLE',
  UNCHANGED = 'UNCHANGED',
  STABLE_DISEASE = 'STABLE_DISEASE',
  RECURRENCE = 'RECURRENCE',
  PROGRESSION = 'PROGRESSION',
}

export enum FinalOutcome {
  NO_EVIDENCE_OF_DISEASE = 'NO_EVIDENCE_OF_DISEASE',
  STABLE_DISEASE = 'STABLE_DISEASE',
  PROGRESSION = 'PROGRESSION',
  RECURRENCE = 'RECURRENCE',
  DECEASED = 'DECEASED',
  LOST_TO_FOLLOWUP = 'LOST_TO_FOLLOWUP',
  IN_TREATMENT = 'IN_TREATMENT',
}

// ============================================================================
// DermatoForm
// ============================================================================

export interface Lesion {
  id?: string;
  type: LesionType;
  customType?: string;
  location: string;
  size: string;
  color?: string;
  border?: string;
  surface?: string;
  symptoms?: string[];
  photoIds?: string[];
  description?: string;
}

export type DermatoSection = Record<string, unknown>;

export interface DermatoForm {
  fotofinderIndicated?: boolean | null;
  fotofinderReportDate?: string | null;
  id: string;
  patientId: string;
  userId: string;
  createdBy: string;
  status: FormStatus;
  phase?: CarePhase;
  programType: string;
  personalHistory?: string;
  familyHistory?: string;
  medications?: string[];
  allergies?: string[];
  generalData?: DermatoSection;
  medicalHistoryDetails?: DermatoSection;
  familyHistoryDetails?: DermatoSection;
  skinType: SkinType;
  photoSensitivity: PhotoSensitivity;
  previousTreatments?: string[];
  uvRiskFactors?: DermatoSection;
  skinEvaluation?: DermatoSection;
  lifestyle?: DermatoSection;
  consent?: DermatoSection;
  /** Sursa trimiterii — folosită în scrisorile medicale („Către: …"). */
  referringDoctorName?: string;
  referringUnit?: string;
  chiefComplaint: string;
  symptomsOnset?: string;
  symptomsDuration?: string;
  lesions?: Lesion[];
  lesionCount: number;
  photoIds?: string[];
  notes?: string;
  reviewedBy?: string;
  reviewedAt?: string;
  reviewNotes?: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
}

export interface CreateDermatoFormDto {
  patientId: string;
  programType: string;
  personalHistory?: string;
  familyHistory?: string;
  medications?: string[];
  allergies?: string[];
  generalData?: DermatoSection;
  medicalHistoryDetails?: DermatoSection;
  familyHistoryDetails?: DermatoSection;
  skinType?: SkinType;
  photoSensitivity?: PhotoSensitivity;
  previousTreatments?: string[];
  uvRiskFactors?: DermatoSection;
  skinEvaluation?: DermatoSection;
  lifestyle?: DermatoSection;
  consent?: DermatoSection;
  /** Sursa trimiterii — folosită în scrisorile medicale („Către: …"). */
  referringDoctorName?: string;
  referringUnit?: string;
  chiefComplaint?: string;
  symptomsOnset?: string;
  symptomsDuration?: string;
  lesions?: Omit<Lesion, 'id'>[];
  photoIds?: string[];
  notes?: string;
}

export interface UpdateDermatoFormDto {
  personalHistory?: string;
  familyHistory?: string;
  medications?: string[];
  allergies?: string[];
  generalData?: DermatoSection;
  medicalHistoryDetails?: DermatoSection;
  familyHistoryDetails?: DermatoSection;
  skinType?: SkinType;
  photoSensitivity?: PhotoSensitivity;
  previousTreatments?: string[];
  uvRiskFactors?: DermatoSection;
  skinEvaluation?: DermatoSection;
  lifestyle?: DermatoSection;
  consent?: DermatoSection;
  /** Sursa trimiterii — folosită în scrisorile medicale („Către: …"). */
  referringDoctorName?: string;
  referringUnit?: string;
  chiefComplaint?: string;
  symptomsOnset?: string;
  symptomsDuration?: string;
  lesions?: Lesion[];
  photoIds?: string[];
  notes?: string;
}

export type TemplatePlaceholderStatus = 'collected' | 'derived' | 'missing-ui' | 'signature' | 'ignored-phase';

export interface TemplatePlaceholder {
  key: string;
  section: string;
  status: TemplatePlaceholderStatus;
  source: string;
}

export interface TemplatePlaceholderResponse {
  programType: 'DERMATO' | 'HEPATO';
  template: string;
  scope: string;
  placeholders: TemplatePlaceholder[];
  totals: Record<TemplatePlaceholderStatus | 'all' | 'active' | 'ignored', number>;
}

/**
 * D1 — Progresul real al formularului pe cei 15 pași (server-side).
 * `steps` e indexat 0-based, în ordinea DERMATO_STEPS din frontend.
 */
export interface DermatoFormProgress {
  steps: boolean[];
  completedCount: number;
  totalSteps: number;
  hasLesions: boolean;
  lesionCount: number;
}

export interface DermatoAuditEntry {
  id: string;
  formId: string;
  userId: string;
  userName: string;
  action: string;
  changes?: Record<string, { before?: unknown; after?: unknown }> | null;
  metadata?: Record<string, unknown> | null;
  createdAt: string;
}

// ============================================================================
// Lesion (full model — not the lightweight one in DermatoForm.lesions)
// ============================================================================

export interface LesionFull {
  id: string;
  formId: string;
  clinicalCode: string;
  sequence: number;
  type: LesionType;
  customType?: string | null;
  location: string;
  laterality?: Laterality | null;
  fitzpatrickType?: SkinType | null;
  size: string;
  color?: string | null;
  border?: string | null;
  surface?: string | null;
  symptoms: string[];
  onsetDate?: string | null;
  recentChange: boolean;
  isNewlyDiagnosed: boolean;
  status: LesionStatus;
  photoIds: string[];
  description?: string | null;
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
}

export interface CreateLesionDto {
  formId: string;
  clinicalCode?: string;
  type?: LesionType;
  customType?: string;
  location: string;
  laterality?: Laterality;
  fitzpatrickType?: SkinType;
  size: string;
  color?: string;
  border?: string;
  surface?: string;
  symptoms?: string[];
  onsetDate?: string;
  recentChange?: boolean;
  isNewlyDiagnosed?: boolean;
  status?: LesionStatus;
  photoIds?: string[];
  description?: string;
  notes?: string;
}

export interface UpdateLesionDto {
  type?: LesionType;
  customType?: string;
  location?: string;
  laterality?: Laterality;
  fitzpatrickType?: SkinType;
  size?: string;
  color?: string;
  border?: string;
  surface?: string;
  symptoms?: string[];
  onsetDate?: string;
  recentChange?: boolean;
  isNewlyDiagnosed?: boolean;
  status?: LesionStatus;
  photoIds?: string[];
  description?: string;
  notes?: string;
}

// ============================================================================
// AbcdeEvaluation
// ============================================================================

export interface AbcdeEvaluation {
  id: string;
  lesionId: string;
  evaluationDate: string;
  performedBy: string;
  asymmetryAbsent: boolean;
  asymmetryPresent: boolean;
  borderRegular: boolean;
  borderIrregular: boolean;
  colorUniform: boolean;
  colorNonUniform: boolean;
  diameterLess6mm: boolean;
  diameter6mm: boolean;
  evolutionStable: boolean;
  evolutionModified: boolean;
  conclusion: AbcdeConclusion;
  observations?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAbcdeDto {
  evaluationDate: string;
  performedBy: string;
  asymmetryAbsent: boolean;
  asymmetryPresent: boolean;
  borderRegular: boolean;
  borderIrregular: boolean;
  colorUniform: boolean;
  colorNonUniform: boolean;
  diameterLess6mm: boolean;
  diameter6mm: boolean;
  evolutionStable: boolean;
  evolutionModified: boolean;
  conclusion: AbcdeConclusion;
  observations?: string;
}

export type UpdateAbcdeDto = Partial<CreateAbcdeDto>;

// ============================================================================
// Dermatoscopy
// ============================================================================

export interface DermatoscopyExam {
  id: string;
  lesionId: string;
  examDate: string;
  performedBy: string;
  device: Device;
  description: string;
  result: DermatoscopyResult;
  recommendation?: string | null;
  imageIds: string[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateDermatoscopyDto {
  examDate: string;
  performedBy: string;
  device: Device;
  description: string;
  result: DermatoscopyResult;
  recommendation?: string;
  imageIds?: string[];
}

export type UpdateDermatoscopyDto = Partial<CreateDermatoscopyDto>;

// ============================================================================
// Video-AI
// ============================================================================

export interface VideoDermatoscopyAi {
  id: string;
  lesionId: string;
  examDate: string;
  device: string;
  algorithmVersion: string;
  aiClassification: AiClassification;
  aiProbability: number;
  aiObservations: string;
  medicalConfirmation: AiMedicalConfirmation;
  conclusion: string;
  recommendedAction: string;
  imageIds: string[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateVideoAiDto {
  examDate: string;
  device: string;
  algorithmVersion: string;
  aiClassification: AiClassification;
  aiProbability: number;
  aiObservations: string;
  medicalConfirmation: AiMedicalConfirmation;
  conclusion: string;
  recommendedAction: string;
  imageIds?: string[];
}

export type UpdateVideoAiDto = Partial<CreateVideoAiDto>;

// ============================================================================
// Total Body
// ============================================================================

export interface BodyMapMarker {
  lesionId: string;
  x: number;
  y: number;
  severity?: string;
}

export interface TotalBodyPhoto {
  id: string;
  formId: string;
  sessionDate: string;
  device: string;
  performedBy: string;
  lesionsIdentified: number;
  markers: { bodyMapMarkers: BodyMapMarker[] };
  previousSessionId?: string | null;
  comparisonNotes?: string | null;
  imageIds: string[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateTotalBodyDto {
  formId: string;
  sessionDate: string;
  device: string;
  performedBy: string;
  lesionsIdentified: number;
  markers: { bodyMapMarkers: BodyMapMarker[] };
  previousSessionId?: string;
  comparisonNotes?: string;
  imageIds?: string[];
}

export type UpdateTotalBodyDto = Partial<CreateTotalBodyDto>;

// ============================================================================
// Biopsy
// ============================================================================

export interface Biopsy {
  id: string;
  lesionId: string;
  biopsyDate: string;
  procedureType: BiopsyProcedureType;
  location: string;
  performedBy: string;
  sampleType: string;
  sampleCode: string;
  laboratory: string;
  sentDate?: string | null;
  sentTo?: string | null;
  documentIds: string[];
  observations?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateBiopsyDto {
  biopsyDate: string;
  procedureType: BiopsyProcedureType;
  location: string;
  performedBy: string;
  sampleType: string;
  sampleCode: string;
  laboratory: string;
  sentDate?: string;
  sentTo?: string;
  documentIds?: string[];
  observations?: string;
}

export type UpdateBiopsyDto = Partial<CreateBiopsyDto>;

// ============================================================================
// HistopathologyDiagnostic (1:1 per biopsy)
// ============================================================================

export interface HistopathologyDiagnostic {
  id: string;
  biopsyId: string;
  resultDate: string;
  pathologist: string;
  sampleType: string;
  sampleCode: string;
  diagnosis: string;
  tumorType: HistoTumorType;
  breslowMm?: number | null;
  ulceration?: boolean | null;
  margins: MarginStatus;
  observations?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateHistopathologyDto {
  resultDate: string;
  pathologist: string;
  sampleType: string;
  sampleCode: string;
  diagnosis: string;
  tumorType: HistoTumorType;
  breslowMm?: number;
  ulceration?: boolean;
  margins: MarginStatus;
  observations?: string;
}

export type UpdateHistopathologyDto = Partial<CreateHistopathologyDto>;

// ============================================================================
// Diagnosis (1:1 per lesion)
// ============================================================================

export interface Diagnosis {
  id: string;
  lesionId: string;
  diagnosisDate: string;
  diagnosis: string;
  tumorType: HistoTumorType;
  confirmed: boolean;
  location: string;
  ajccStage?: AjccStage | null;
  breslowMm?: number | null;
  ulceration?: boolean | null;
  classificationUsed?: string | null;
  extent?: string | null;
  diagnosedBy: string;
  conclusion: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateDiagnosisDto {
  diagnosisDate: string;
  diagnosis: string;
  tumorType: HistoTumorType;
  confirmed?: boolean;
  location: string;
  ajccStage?: AjccStage;
  breslowMm?: number;
  ulceration?: boolean;
  classificationUsed?: string;
  extent?: string;
  diagnosedBy: string;
  conclusion: string;
}

export type UpdateDiagnosisDto = Partial<CreateDiagnosisDto>;

// ============================================================================
// TreatmentPlan (1:N per lesion)
// ============================================================================

export interface TreatmentPlan {
  id: string;
  lesionId: string;
  decisionDate: string;
  treatmentType: string;
  intent: TreatmentIntent;
  status: TreatmentStatus;
  excisionChosen: boolean;
  immunotherapyChosen: boolean;
  otherTreatmentChosen: boolean;
  scheduledDate?: string | null;
  team?: string | null;
  consentObtained: boolean;
  observations?: string | null;
  decisionText: string;
  /**
   * §1.3 — rezumatul deciziei comisiei oncologice (transcrisă de medic).
   * Câmpul vechi `commissionDecision` a fost eliminat din model.
   */
  commissionOutcomeSummary?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTreatmentPlanDto {
  decisionDate: string;
  treatmentType: string;
  intent: TreatmentIntent;
  status: TreatmentStatus;
  excisionChosen?: boolean;
  immunotherapyChosen?: boolean;
  otherTreatmentChosen?: boolean;
  scheduledDate?: string;
  team?: string;
  consentObtained?: boolean;
  observations?: string;
  decisionText: string;
}

export type UpdateTreatmentPlanDto = Partial<CreateTreatmentPlanDto>;

// ============================================================================
// Surgery (1:N per lesion)
// ============================================================================

export interface Surgery {
  id: string;
  lesionId: string;
  surgeryDate: string;
  procedureType: SurgeryProcedureType;
  performedBy: string;
  unit: string;
  location: string;
  specimenSize?: number | null;
  plannedMargin?: string | null;
  completed: boolean;
  complications?: PostOpComplication | null;
  postOpStatus?: string | null;
  recommendations?: string | null;
  dischargeDate?: string | null;
  surgicalNotes?: string | null;
  treatmentPlanId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSurgeryDto {
  surgeryDate: string;
  procedureType: SurgeryProcedureType;
  performedBy: string;
  unit: string;
  location: string;
  specimenSize?: number;
  plannedMargin?: string;
  completed?: boolean;
  complications?: PostOpComplication;
  postOpStatus?: string;
  recommendations?: string;
  dischargeDate?: string;
  surgicalNotes?: string;
  treatmentPlanId?: string;
}

export type UpdateSurgeryDto = Partial<CreateSurgeryDto>;

// ============================================================================
// SurgicalHistopathology (1:1 per surgery)
// ============================================================================

export interface SurgicalHistopathology {
  id: string;
  surgeryId: string;
  specimenCode: string;
  resultDate: string;
  pathologist: string;
  specimenType: string;
  definitiveDiagnosis: string;
  margins: MarginStatus;
  residualTumor: ResidualTumor;
  reintervention: ReinterventionNeeded;
  observations?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSurgicalHistopathologyDto {
  specimenCode: string;
  resultDate: string;
  pathologist: string;
  specimenType: string;
  definitiveDiagnosis: string;
  margins: MarginStatus;
  residualTumor: ResidualTumor;
  reintervention: ReinterventionNeeded;
  observations?: string;
}

export type UpdateSurgicalHistopathologyDto = Partial<CreateSurgicalHistopathologyDto>;

// ============================================================================
// FollowUpVisit (1:N per lesion)
// ============================================================================

export interface FollowUpVisit {
  id: string;
  lesionId: string;
  visitDate: string;
  status: FollowUpStatus;
  recurrence: boolean;
  newLesions: boolean;
  nextVisitDate?: string | null;
  actions?: string | null;
  performedBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateFollowUpVisitDto {
  visitDate: string;
  status: FollowUpStatus;
  recurrence?: boolean;
  newLesions?: boolean;
  nextVisitDate?: string;
  actions?: string;
  performedBy: string;
}

export type UpdateFollowUpVisitDto = Partial<CreateFollowUpVisitDto>;

// ============================================================================
// PatientOutcome (1:1 per lesion)
// ============================================================================

export interface PatientOutcome {
  id: string;
  lesionId: string;
  evaluationDate: string;
  status: FinalOutcome;
  lastContactDate?: string | null;
  inFollowUp: boolean;
  observations?: string | null;
  evaluatedBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePatientOutcomeDto {
  evaluationDate: string;
  status: FinalOutcome;
  lastContactDate?: string;
  inFollowUp?: boolean;
  observations?: string;
  evaluatedBy: string;
}

export type UpdatePatientOutcomeDto = Partial<CreatePatientOutcomeDto>;

// ============================================================================
// D2.3 — Screening Result (Step 8)
// ============================================================================

export interface DermatoScreeningResult {
  id: string;
  formId: string;
  evaluationSummary: Record<string, unknown>;
  screeningConclusion: ScreeningConclusion;
  recommendation?: string | null;
  observations?: string | null;
  referralToBiopsy: boolean;
  isManualOverride: boolean;
  overrideReason?: string | null;
  evaluatedAt: string;
  evaluatedBy: string;
  /** Status pentru o eventuală revenire (setat la finalizarea fără leziuni). */
  returnStatus?: ReturnStatus | null;
  recontrolIntervalMonths?: number | null;
  nextRecontrolDate?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UpsertScreeningResultDto {
  evaluationSummary: Record<string, unknown>;
  screeningConclusion: ScreeningConclusion;
  recommendation?: string;
  observations?: string;
  referralToBiopsy?: boolean;
  isManualOverride?: boolean;
  overrideReason?: string;
  evaluatedBy: string;
  /** Se suprascriu doar când sunt trimise (altfel se păstrează valorile existente). */
  returnStatus?: ReturnStatus;
  recontrolIntervalMonths?: number;
  nextRecontrolDate?: string;
}

export interface FinalizeNoLesionsDto {
  /** Intervalul de recontrol recomandat, în luni (default 12). */
  recontrolIntervalMonths?: number;
}

export interface FinalizeNoLesionsResult {
  screeningResult: DermatoScreeningResult;
  returnStatus: ReturnStatus;
  recontrolIntervalMonths: number;
  nextRecontrolDate: string;
  letter: { documentId: string; fileName: string; version: number };
}

export interface CreateBiopsyDraftFromScreeningDto {
  procedureType?: BiopsyProcedureType;
  performedBy?: string;
  sampleType?: string;
  sampleCode?: string;
  laboratory?: string;
  observations?: string;
}

// ============================================================================
// Paginated response
// ============================================================================

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

// ============================================================================
// Nomenclatură tumorală (§2.1.4)
// Căutarea este deja case- și diacritic-insenzibilă pe server.
// ============================================================================

export enum TumorNomenclatureLayer {
  NON_MELANOCYTIC_BENIGN = 'NON_MELANOCYTIC_BENIGN',
  NON_MELANOCYTIC_PREMALIGNANT = 'NON_MELANOCYTIC_PREMALIGNANT',
  NON_MELANOCYTIC_MALIGNANT = 'NON_MELANOCYTIC_MALIGNANT',
  MELANOCYTIC_BENIGN = 'MELANOCYTIC_BENIGN',
  MELANOCYTIC_PREMALIGNANT = 'MELANOCYTIC_PREMALIGNANT',
  MELANOCYTIC_MALIGNANT = 'MELANOCYTIC_MALIGNANT',
  MELANOCYTIC_BORDERLINE = 'MELANOCYTIC_BORDERLINE',
}

export interface TumorNomenclatureItem {
  id: string;
  code: string;
  labelRo: string;
  labelEn: string | null;
  layer: TumorNomenclatureLayer;
  parentId: string | null;
}

export interface TumorNomenclatureGroup {
  layer: TumorNomenclatureLayer;
  items: TumorNomenclatureItem[];
}

export interface TumorNomenclatureSearchResult {
  items: TumorNomenclatureItem[];
  grouped: TumorNomenclatureGroup[];
  total: number;
}

// ============================================================================
// Cronologie formular (§2.10.1–§2.10.3)
// ============================================================================

export enum TimelineEventType {
  FLOW_EVENT = 'FLOW_EVENT',
  DOCUMENT_UPLOADED = 'DOCUMENT_UPLOADED',
  DOCUMENT_GENERATED = 'DOCUMENT_GENERATED',
  COMMISSION_DECISION = 'COMMISSION_DECISION',
  TASK = 'TASK',
}

export interface TimelineEvent {
  id: string;
  type: string;
  occurredAt: string;
  title: string;
  description: string | null;
  actor: string | null;
  link: string | null;
  /** Doar pentru DOCUMENT_UPLOADED — permite descărcarea directă din cronologie. */
  documentId?: string | null;
  /** Doar pentru DOCUMENT_UPLOADED. */
  fileName?: string | null;
}

export interface TimelineResponse {
  items: TimelineEvent[];
  total: number;
  page: number;
  pageSize: number;
}

// ============================================================================
// Import automat din document (§2.3.1–§2.3.4)
// ============================================================================

export type ImportConfidence = 'high' | 'medium' | 'low';

export interface ImportExtractedField {
  value: string | number | boolean | null;
  unit: string | null;
  source: string | null;
}

export interface ImportProposal {
  documentId: string;
  documentKind: string;
  fileName: string;
  model: string;
  fields: Record<string, ImportExtractedField>;
  confidence: ImportConfidence;
  warnings: string[];
}

export interface ImportConfirmResult {
  documentId: string;
  written: string[];
  skipped: Array<{ field: string; reason: string }>;
  auditId: string;
}

/** Document încărcat pe formular (listă scurtă, pentru import). */
export interface DermatoFormDocument {
  id: string;
  kind: string;
  fileName: string;
  mimeType?: string | null;
  sizeBytes?: number | null;
  lesionId?: string | null;
  uploadedBy?: string | null;
  uploadedAt?: string | null;
}

// ============================================================================
// Helpers
// ============================================================================

// Backend poate răspunde fie {data: X} (sau {data: {data: X}}), fie direct X.
const unwrap = <T,>(response: any): T => response?.data?.data ?? response?.data ?? response;

const buildPaginated = <T,>(responseData: any, fallback?: { page?: number; limit?: number }): PaginatedResponse<T> => {
  const payload = responseData?.data && !Array.isArray(responseData.data)
    ? responseData.data
    : responseData;
  const items: T[] = Array.isArray(payload) ? payload : payload?.data ?? [];
  const total = payload?.total ?? responseData?.meta?.total ?? items.length;
  const page = payload?.page ?? responseData?.meta?.page ?? fallback?.page ?? 1;
  const limit = payload?.limit ?? responseData?.meta?.limit ?? fallback?.limit ?? 20;
  return { data: items, total, page, limit };
};

// ============================================================================
// API methods
// ============================================================================

const dermatoApi = {
  // DermatoForm ------------------------------------------------------------
  async create(data: CreateDermatoFormDto): Promise<DermatoForm> {
    const r = await apiClient.post('/dermato-forms', data);
    return unwrap<DermatoForm>(r.data);
  },
  async getById(id: string): Promise<DermatoForm> {
    const r = await apiClient.get(`/dermato-forms/${id}`);
    return unwrap<DermatoForm>(r.data);
  },
  async update(id: string, data: UpdateDermatoFormDto): Promise<DermatoForm> {
    const r = await apiClient.patch(`/dermato-forms/${id}`, data);
    return unwrap<DermatoForm>(r.data);
  },
  async submit(id: string): Promise<DermatoForm> {
    const r = await apiClient.post(`/dermato-forms/${id}/submit`);
    return unwrap<DermatoForm>(r.data);
  },

  /**
   * Finalizare fără leziuni (etapa 4 fără inventar): verdict NO_LESIONS,
   * status de revenire (recontrol 12 luni default), scrisoare medicală către
   * medicul de familie și trimiterea formularului spre revizuire.
   */
  async finalizeNoLesions(id: string, data?: FinalizeNoLesionsDto): Promise<FinalizeNoLesionsResult> {
    const r = await apiClient.post(`/dermato-forms/${id}/finalize-no-lesions`, data ?? {});
    return unwrap<FinalizeNoLesionsResult>(r.data);
  },
  async list(params?: {
    page?: number;
    limit?: number;
    patientId?: string;
    status?: FormStatus;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    createdAfter?: string;
    createdBefore?: string;
  }): Promise<PaginatedResponse<DermatoForm>> {
    const r = await apiClient.get('/dermato-forms', { params });
    return buildPaginated<DermatoForm>(r.data, params);
  },
  async getTemplatePlaceholders(lang?: DocumentLanguage): Promise<TemplatePlaceholderResponse> {
    const r = await apiClient.get('/dermato-forms/template-placeholders', { params: lang ? { lang } : undefined });
    return unwrap<TemplatePlaceholderResponse>(r.data);
  },
  /** D1 — Progresul real pe cei 15 pași (pentru bifele din sidebar și pagina pacientului). */
  async getProgress(id: string): Promise<DermatoFormProgress> {
    const r = await apiClient.get(`/dermato-forms/${id}/progress`);
    return unwrap<DermatoFormProgress>(r.data);
  },
  /** D1 — Istoricul audit al formularului. */
  async getAuditTrail(id: string): Promise<DermatoAuditEntry[]> {
    const r = await apiClient.get(`/dermato-forms/${id}/audit`);
    const v = unwrap<DermatoAuditEntry[] | { data: DermatoAuditEntry[] }>(r.data);
    return Array.isArray(v) ? v : v?.data ?? [];
  },
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/dermato-forms/${id}`);
  },

  /**
   * F1.8 — Descarcă PDF-ul complet (secțiunile I-XIII) în limba specificată.
   * Returnează Blob pentru a putea fi salvat local de către UI.
   * `lang` = limba DOCUMENTULUI (RO / EN / SRB). Opțional: dacă lipsește,
   * backendul rezolvă limba (siteCode → RO).
   */
  async downloadFullPdf(id: string, lang?: DocumentLanguage): Promise<Blob> {
    const r = await apiClient.get(`/dermato-forms/${id}/export-full.pdf`, {
      params: lang ? { lang } : undefined,
      responseType: 'blob',
    });
    return r.data as Blob;
  },

  /**
   * F1.8 — PDF complet + metadata despre limba rezolvată (§1.0c.5/§1.0c.6).
   * Backendul trimite metadata în headerele răspunsului:
   *   x-document-language   → limba rezolvată
   *   x-requested-language  → limba cerută
   *   x-language-fallback   → 'true' dacă s-a folosit fallback-ul
   *   x-fallback-reason     → motivul fallback-ului
   *   x-language-message    → mesajul de banner, gata de afișat
   */
  async downloadFullPdfWithMeta(id: string, lang?: DocumentLanguage): Promise<DocumentDownloadResult> {
    const r = await apiClient.get(`/dermato-forms/${id}/export-full.pdf`, {
      params: lang ? { lang } : undefined,
      responseType: 'blob',
    });
    return { blob: r.data as Blob, meta: parseDocumentLanguageMeta(r.headers) };
  },

  /**
   * F1.8 — Descarcă PDF-ul de anamneză (secțiunile I-VI) în limba specificată.
   */
  async downloadAnamnesisPdf(id: string, lang?: DocumentLanguage): Promise<Blob> {
    const r = await apiClient.get(`/dermato-forms/${id}/export.pdf`, {
      params: lang ? { lang } : undefined,
      responseType: 'blob',
    });
    return r.data as Blob;
  },

  /** PDF de anamneză + metadata despre limba rezolvată (§1.0c.5/§1.0c.6). */
  async downloadAnamnesisPdfWithMeta(id: string, lang?: DocumentLanguage): Promise<DocumentDownloadResult> {
    const r = await apiClient.get(`/dermato-forms/${id}/export.pdf`, {
      params: lang ? { lang } : undefined,
      responseType: 'blob',
    });
    return { blob: r.data as Blob, meta: parseDocumentLanguageMeta(r.headers) };
  },

  /**
   * §1.2.6 — Scrisoare către dermatologie (`GET /dermato-forms/:id/referral-dermato.pdf`).
   * `lang` = limba documentului; `lesionId` = leziunea de referit (opțional).
   */
  async downloadReferralDermatology(
    id: string,
    lang?: DocumentLanguage,
    lesionId?: string,
  ): Promise<DocumentDownloadResult> {
    return downloadMedicalDocumentFile(id, 'referral-dermato.pdf', lang, lesionId);
  },

  /**
   * §1.2.6 — Scrisoare către chirurg (`GET /dermato-forms/:id/referral-surgery.pdf`).
   * `lang` = limba documentului; `lesionId` = leziunea de referit (opțional).
   */
  async downloadReferralSurgery(
    id: string,
    lang?: DocumentLanguage,
    lesionId?: string,
  ): Promise<DocumentDownloadResult> {
    return downloadMedicalDocumentFile(id, 'referral-surgery.pdf', lang, lesionId);
  },

  /**
   * §1.2.6 — Fișă comisie oncologică (`GET /dermato-forms/:id/commission-sheet.pdf`).
   * `lang` = limba documentului; `lesionId` = leziunea de referit (opțional).
   */
  async downloadCommissionSheet(
    id: string,
    lang?: DocumentLanguage,
    lesionId?: string,
  ): Promise<DocumentDownloadResult> {
    return downloadMedicalDocumentFile(id, 'commission-sheet.pdf', lang, lesionId);
  },

  /**
   * Scrisoare medicală — screening finalizat fără leziuni (către medicul de
   * familie). Nu are leziune asociată.
   */
  async downloadScreeningLetter(
    id: string,
    lang?: DocumentLanguage,
  ): Promise<DocumentDownloadResult> {
    return downloadMedicalDocumentFile(id, 'screening-letter.pdf', lang);
  },

  // Lesions ---------------------------------------------------------------
  lesions: {
    async create(data: CreateLesionDto): Promise<LesionFull> {
      const r = await apiClient.post('/dermato/lesions', data);
      return unwrap<LesionFull>(r.data);
    },
    async getById(id: string): Promise<LesionFull> {
      const r = await apiClient.get(`/dermato/lesions/${id}`);
      return unwrap<LesionFull>(r.data);
    },
    async update(id: string, data: UpdateLesionDto): Promise<LesionFull> {
      const r = await apiClient.patch(`/dermato/lesions/${id}`, data);
      return unwrap<LesionFull>(r.data);
    },
    async delete(id: string): Promise<void> {
      await apiClient.delete(`/dermato/lesions/${id}`);
    },
    async list(params?: {
      page?: number;
      limit?: number;
      formId?: string;
      patientId?: string;
      status?: LesionStatus;
      type?: LesionType;
      laterality?: Laterality;
      includeDeleted?: boolean;
    }): Promise<PaginatedResponse<LesionFull>> {
      const r = await apiClient.get('/dermato/lesions', { params });
      return buildPaginated<LesionFull>(r.data, params);
    },
  },

  // D2.3 — Screening Result (Step 8) --------------------------------------
  screeningResult: {
    async getByForm(formId: string): Promise<DermatoScreeningResult | null> {
      const r = await apiClient.get(`/dermato-forms/${formId}/screening-result`);
      const v = unwrap<DermatoScreeningResult | null>(r.data);
      return v ?? null;
    },
    async upsert(formId: string, data: UpsertScreeningResultDto): Promise<DermatoScreeningResult> {
      const r = await apiClient.put(`/dermato-forms/${formId}/screening-result`, data);
      return unwrap<DermatoScreeningResult>(r.data);
    },
    /**
     * D2.3 — Creează un DRAFT de biopsie pentru o leziune (de pe butonul
     * „Trimite la biopsie" din Step 8). NU aplică reguli de prerechizit.
     */
    async createBiopsyDraft(
      formId: string,
      lesionId: string,
      data: CreateBiopsyDraftFromScreeningDto = {},
    ): Promise<Biopsy> {
      const r = await apiClient.post(
        `/dermato-forms/${formId}/lesions/${lesionId}/biopsy-draft`,
        data,
      );
      return unwrap<Biopsy>(r.data);
    },
  },

  // ABCDE -----------------------------------------------------------------
  abcde: {
    async createForLesion(lesionId: string, data: CreateAbcdeDto): Promise<AbcdeEvaluation> {
      const r = await apiClient.post(`/dermato/lesions/${lesionId}/abcde`, data);
      return unwrap<AbcdeEvaluation>(r.data);
    },
    async getByLesion(lesionId: string): Promise<AbcdeEvaluation | null> {
      const r = await apiClient.get(`/dermato/lesions/${lesionId}/abcde`);
      const v = unwrap<AbcdeEvaluation | null>(r.data);
      return v ?? null;
    },
    async getById(id: string): Promise<AbcdeEvaluation> {
      const r = await apiClient.get(`/dermato/abcde/${id}`);
      return unwrap<AbcdeEvaluation>(r.data);
    },
    async update(id: string, data: UpdateAbcdeDto): Promise<AbcdeEvaluation> {
      const r = await apiClient.patch(`/dermato/abcde/${id}`, data);
      return unwrap<AbcdeEvaluation>(r.data);
    },
    async delete(id: string): Promise<void> {
      await apiClient.delete(`/dermato/abcde/${id}`);
    },
    async list(params?: {
      page?: number;
      limit?: number;
      lesionId?: string;
      formId?: string;
      patientId?: string;
      conclusion?: AbcdeConclusion;
    }): Promise<PaginatedResponse<AbcdeEvaluation>> {
      const r = await apiClient.get('/dermato/abcde', { params });
      return buildPaginated<AbcdeEvaluation>(r.data, params);
    },
  },

  // Dermatoscopy ----------------------------------------------------------
  dermatoscopy: {
    async createForLesion(lesionId: string, data: CreateDermatoscopyDto): Promise<DermatoscopyExam> {
      const r = await apiClient.post(`/dermato/lesions/${lesionId}/dermatoscopy`, data);
      return unwrap<DermatoscopyExam>(r.data);
    },
    async getByLesion(lesionId: string): Promise<DermatoscopyExam | null> {
      const r = await apiClient.get(`/dermato/lesions/${lesionId}/dermatoscopy`);
      const v = unwrap<DermatoscopyExam | null>(r.data);
      return v ?? null;
    },
    async getById(id: string): Promise<DermatoscopyExam> {
      const r = await apiClient.get(`/dermato/dermatoscopy/${id}`);
      return unwrap<DermatoscopyExam>(r.data);
    },
    async update(id: string, data: UpdateDermatoscopyDto): Promise<DermatoscopyExam> {
      const r = await apiClient.patch(`/dermato/dermatoscopy/${id}`, data);
      return unwrap<DermatoscopyExam>(r.data);
    },
    async delete(id: string): Promise<void> {
      await apiClient.delete(`/dermato/dermatoscopy/${id}`);
    },
    async list(params?: {
      page?: number;
      limit?: number;
      lesionId?: string;
      formId?: string;
      result?: DermatoscopyResult;
      device?: Device;
      performedBy?: string;
    }): Promise<PaginatedResponse<DermatoscopyExam>> {
      const r = await apiClient.get('/dermato/dermatoscopy', { params });
      return buildPaginated<DermatoscopyExam>(r.data, params);
    },
  },

  // Video-AI --------------------------------------------------------------
  videoAi: {
    async createForLesion(lesionId: string, data: CreateVideoAiDto): Promise<VideoDermatoscopyAi> {
      const r = await apiClient.post(`/dermato/lesions/${lesionId}/video-ai`, data);
      return unwrap<VideoDermatoscopyAi>(r.data);
    },
    async getByLesion(lesionId: string): Promise<VideoDermatoscopyAi | null> {
      const r = await apiClient.get(`/dermato/lesions/${lesionId}/video-ai`);
      const v = unwrap<VideoDermatoscopyAi | null>(r.data);
      return v ?? null;
    },
    async getById(id: string): Promise<VideoDermatoscopyAi> {
      const r = await apiClient.get(`/dermato/video-ai/${id}`);
      return unwrap<VideoDermatoscopyAi>(r.data);
    },
    async update(id: string, data: UpdateVideoAiDto): Promise<VideoDermatoscopyAi> {
      const r = await apiClient.patch(`/dermato/video-ai/${id}`, data);
      return unwrap<VideoDermatoscopyAi>(r.data);
    },
    async delete(id: string): Promise<void> {
      await apiClient.delete(`/dermato/video-ai/${id}`);
    },
    async list(params?: {
      page?: number;
      limit?: number;
      lesionId?: string;
      aiClassification?: AiClassification;
      medicalConfirmation?: AiMedicalConfirmation;
    }): Promise<PaginatedResponse<VideoDermatoscopyAi>> {
      const r = await apiClient.get('/dermato/video-ai', { params });
      return buildPaginated<VideoDermatoscopyAi>(r.data, params);
    },
  },

  // Total Body ------------------------------------------------------------
  totalBody: {
    async create(data: CreateTotalBodyDto): Promise<TotalBodyPhoto> {
      const r = await apiClient.post('/dermato/total-body', data);
      return unwrap<TotalBodyPhoto>(r.data);
    },
    async getById(id: string): Promise<TotalBodyPhoto> {
      const r = await apiClient.get(`/dermato/total-body/${id}`);
      return unwrap<TotalBodyPhoto>(r.data);
    },
    async getByForm(formId: string): Promise<TotalBodyPhoto[]> {
      const r = await apiClient.get(`/dermato/forms/${formId}/total-body`);
      const v = unwrap<TotalBodyPhoto[] | { data: TotalBodyPhoto[] }>(r.data);
      return Array.isArray(v) ? v : v?.data ?? [];
    },
    async update(id: string, data: UpdateTotalBodyDto): Promise<TotalBodyPhoto> {
      const r = await apiClient.patch(`/dermato/total-body/${id}`, data);
      return unwrap<TotalBodyPhoto>(r.data);
    },
    async delete(id: string): Promise<void> {
      await apiClient.delete(`/dermato/total-body/${id}`);
    },
    async list(params?: {
      page?: number;
      limit?: number;
      formId?: string;
      performedBy?: string;
    }): Promise<PaginatedResponse<TotalBodyPhoto>> {
      const r = await apiClient.get('/dermato/total-body', { params });
      return buildPaginated<TotalBodyPhoto>(r.data, params);
    },
  },

  // Biopsy (1:N per lesion) ----------------------------------------------
  biopsy: {
    async createForLesion(lesionId: string, data: CreateBiopsyDto): Promise<Biopsy> {
      const r = await apiClient.post(`/dermato/lesions/${lesionId}/biopsies`, data);
      return unwrap<Biopsy>(r.data);
    },
    async getById(id: string): Promise<Biopsy> {
      const r = await apiClient.get(`/dermato/biopsies/${id}`);
      return unwrap<Biopsy>(r.data);
    },
    async getByLesion(lesionId: string): Promise<Biopsy[]> {
      const r = await apiClient.get(`/dermato/lesions/${lesionId}/biopsies`);
      const v = unwrap<Biopsy[] | { data: Biopsy[] }>(r.data);
      return Array.isArray(v) ? v : v?.data ?? [];
    },
    async update(id: string, data: UpdateBiopsyDto): Promise<Biopsy> {
      const r = await apiClient.patch(`/dermato/biopsies/${id}`, data);
      return unwrap<Biopsy>(r.data);
    },
    async delete(id: string): Promise<void> {
      await apiClient.delete(`/dermato/biopsies/${id}`);
    },
    async list(params?: {
      page?: number;
      limit?: number;
      lesionId?: string;
      formId?: string;
      patientId?: string;
    }): Promise<PaginatedResponse<Biopsy>> {
      const r = await apiClient.get('/dermato/biopsies', { params });
      return buildPaginated<Biopsy>(r.data, params);
    },
  },

  // HistopathologyDiagnostic (1:1 per biopsy) ----------------------------
  histopathology: {
    async createForBiopsy(biopsyId: string, data: CreateHistopathologyDto): Promise<HistopathologyDiagnostic> {
      const r = await apiClient.post(`/dermato/biopsies/${biopsyId}/histopathology`, data);
      return unwrap<HistopathologyDiagnostic>(r.data);
    },
    async getById(id: string): Promise<HistopathologyDiagnostic> {
      const r = await apiClient.get(`/dermato/histopathology/${id}`);
      return unwrap<HistopathologyDiagnostic>(r.data);
    },
    async getByBiopsy(biopsyId: string): Promise<HistopathologyDiagnostic | null> {
      const r = await apiClient.get(`/dermato/biopsies/${biopsyId}/histopathology`);
      const v = unwrap<HistopathologyDiagnostic | null>(r.data);
      return v ?? null;
    },
    async update(id: string, data: UpdateHistopathologyDto): Promise<HistopathologyDiagnostic> {
      const r = await apiClient.patch(`/dermato/histopathology/${id}`, data);
      return unwrap<HistopathologyDiagnostic>(r.data);
    },
    async delete(id: string): Promise<void> {
      await apiClient.delete(`/dermato/histopathology/${id}`);
    },
    async list(params?: {
      page?: number;
      limit?: number;
      biopsyId?: string;
      tumorType?: HistoTumorType;
    }): Promise<PaginatedResponse<HistopathologyDiagnostic>> {
      const r = await apiClient.get('/dermato/histopathology', { params });
      return buildPaginated<HistopathologyDiagnostic>(r.data, params);
    },
  },

  // Diagnosis (1:1 per lesion) -------------------------------------------
  diagnosis: {
    async createForLesion(lesionId: string, data: CreateDiagnosisDto): Promise<Diagnosis> {
      const r = await apiClient.post(`/dermato/lesions/${lesionId}/diagnosis`, data);
      return unwrap<Diagnosis>(r.data);
    },
    async getByLesion(lesionId: string): Promise<Diagnosis | null> {
      const r = await apiClient.get(`/dermato/lesions/${lesionId}/diagnosis`);
      const v = unwrap<Diagnosis | null>(r.data);
      return v ?? null;
    },
    async getById(id: string): Promise<Diagnosis> {
      const r = await apiClient.get(`/dermato/diagnoses/${id}`);
      return unwrap<Diagnosis>(r.data);
    },
    async update(id: string, data: UpdateDiagnosisDto): Promise<Diagnosis> {
      const r = await apiClient.patch(`/dermato/diagnoses/${id}`, data);
      return unwrap<Diagnosis>(r.data);
    },
    async delete(id: string): Promise<void> {
      await apiClient.delete(`/dermato/diagnoses/${id}`);
    },
    async list(params?: {
      page?: number;
      limit?: number;
      lesionId?: string;
      patientId?: string;
      tumorType?: HistoTumorType;
      ajccStage?: AjccStage;
    }): Promise<PaginatedResponse<Diagnosis>> {
      const r = await apiClient.get('/dermato/diagnoses', { params });
      return buildPaginated<Diagnosis>(r.data, params);
    },
  },

  // TreatmentPlan (1:N per lesion) ---------------------------------------
  treatments: {
    async createForLesion(lesionId: string, data: CreateTreatmentPlanDto): Promise<TreatmentPlan> {
      const r = await apiClient.post(`/dermato/lesions/${lesionId}/treatments`, data);
      return unwrap<TreatmentPlan>(r.data);
    },
    async getById(id: string): Promise<TreatmentPlan> {
      const r = await apiClient.get(`/dermato/treatments/${id}`);
      return unwrap<TreatmentPlan>(r.data);
    },
    async getByLesion(lesionId: string): Promise<TreatmentPlan[]> {
      const r = await apiClient.get(`/dermato/lesions/${lesionId}/treatments`);
      const v = unwrap<TreatmentPlan[] | { data: TreatmentPlan[] }>(r.data);
      return Array.isArray(v) ? v : v?.data ?? [];
    },
    async update(id: string, data: UpdateTreatmentPlanDto): Promise<TreatmentPlan> {
      const r = await apiClient.patch(`/dermato/treatments/${id}`, data);
      return unwrap<TreatmentPlan>(r.data);
    },
    async delete(id: string): Promise<void> {
      await apiClient.delete(`/dermato/treatments/${id}`);
    },
    async list(params?: {
      page?: number;
      limit?: number;
      lesionId?: string;
      patientId?: string;
      status?: TreatmentStatus;
      intent?: TreatmentIntent;
    }): Promise<PaginatedResponse<TreatmentPlan>> {
      const r = await apiClient.get('/dermato/treatments', { params });
      return buildPaginated<TreatmentPlan>(r.data, params);
    },
  },

  // Surgery (1:N per lesion) ---------------------------------------------
  surgeries: {
    async createForLesion(lesionId: string, data: CreateSurgeryDto): Promise<Surgery> {
      const r = await apiClient.post(`/dermato/lesions/${lesionId}/surgeries`, data);
      return unwrap<Surgery>(r.data);
    },
    async getById(id: string): Promise<Surgery> {
      const r = await apiClient.get(`/dermato/surgeries/${id}`);
      return unwrap<Surgery>(r.data);
    },
    async getByLesion(lesionId: string): Promise<Surgery[]> {
      const r = await apiClient.get(`/dermato/lesions/${lesionId}/surgeries`);
      const v = unwrap<Surgery[] | { data: Surgery[] }>(r.data);
      return Array.isArray(v) ? v : v?.data ?? [];
    },
    async update(id: string, data: UpdateSurgeryDto): Promise<Surgery> {
      const r = await apiClient.patch(`/dermato/surgeries/${id}`, data);
      return unwrap<Surgery>(r.data);
    },
    async delete(id: string): Promise<void> {
      await apiClient.delete(`/dermato/surgeries/${id}`);
    },
    async list(params?: {
      page?: number;
      limit?: number;
      lesionId?: string;
      patientId?: string;
      procedureType?: SurgeryProcedureType;
    }): Promise<PaginatedResponse<Surgery>> {
      const r = await apiClient.get('/dermato/surgeries', { params });
      return buildPaginated<Surgery>(r.data, params);
    },
  },

  // SurgicalHistopathology (1:1 per surgery) -----------------------------
  surgicalHistopathology: {
    async createForSurgery(surgeryId: string, data: CreateSurgicalHistopathologyDto): Promise<SurgicalHistopathology> {
      const r = await apiClient.post(`/dermato/surgeries/${surgeryId}/surgical-histopathology`, data);
      return unwrap<SurgicalHistopathology>(r.data);
    },
    async getById(id: string): Promise<SurgicalHistopathology> {
      const r = await apiClient.get(`/dermato/surgical-histopathology/${id}`);
      return unwrap<SurgicalHistopathology>(r.data);
    },
    async getBySurgery(surgeryId: string): Promise<SurgicalHistopathology | null> {
      const r = await apiClient.get(`/dermato/surgeries/${surgeryId}/surgical-histopathology`);
      const v = unwrap<SurgicalHistopathology | null>(r.data);
      return v ?? null;
    },
    async update(id: string, data: UpdateSurgicalHistopathologyDto): Promise<SurgicalHistopathology> {
      const r = await apiClient.patch(`/dermato/surgical-histopathology/${id}`, data);
      return unwrap<SurgicalHistopathology>(r.data);
    },
    async delete(id: string): Promise<void> {
      await apiClient.delete(`/dermato/surgical-histopathology/${id}`);
    },
    async list(params?: {
      page?: number;
      limit?: number;
      surgeryId?: string;
    }): Promise<PaginatedResponse<SurgicalHistopathology>> {
      const r = await apiClient.get('/dermato/surgical-histopathology', { params });
      return buildPaginated<SurgicalHistopathology>(r.data, params);
    },
  },

  // FollowUpVisit (1:N per lesion) ----------------------------------------
  followUps: {
    async createForLesion(lesionId: string, data: CreateFollowUpVisitDto): Promise<FollowUpVisit> {
      const r = await apiClient.post(`/dermato/lesions/${lesionId}/follow-ups`, data);
      return unwrap<FollowUpVisit>(r.data);
    },
    async getById(id: string): Promise<FollowUpVisit> {
      const r = await apiClient.get(`/dermato/follow-ups/${id}`);
      return unwrap<FollowUpVisit>(r.data);
    },
    async getByLesion(lesionId: string): Promise<FollowUpVisit[]> {
      const r = await apiClient.get(`/dermato/lesions/${lesionId}/follow-ups`);
      const v = unwrap<FollowUpVisit[] | { data: FollowUpVisit[] }>(r.data);
      return Array.isArray(v) ? v : v?.data ?? [];
    },
    async update(id: string, data: UpdateFollowUpVisitDto): Promise<FollowUpVisit> {
      const r = await apiClient.patch(`/dermato/follow-ups/${id}`, data);
      return unwrap<FollowUpVisit>(r.data);
    },
    async delete(id: string): Promise<void> {
      await apiClient.delete(`/dermato/follow-ups/${id}`);
    },
    async list(params?: {
      page?: number;
      limit?: number;
      lesionId?: string;
      patientId?: string;
      status?: FollowUpStatus;
    }): Promise<PaginatedResponse<FollowUpVisit>> {
      const r = await apiClient.get('/dermato/follow-ups', { params });
      return buildPaginated<FollowUpVisit>(r.data, params);
    },
  },

  // PatientOutcome (1:1 per lesion) ---------------------------------------
  outcomes: {
    async createForLesion(lesionId: string, data: CreatePatientOutcomeDto): Promise<PatientOutcome> {
      const r = await apiClient.post(`/dermato/lesions/${lesionId}/outcome`, data);
      return unwrap<PatientOutcome>(r.data);
    },
    async getByLesion(lesionId: string): Promise<PatientOutcome | null> {
      const r = await apiClient.get(`/dermato/lesions/${lesionId}/outcome`);
      const v = unwrap<PatientOutcome | null>(r.data);
      return v ?? null;
    },
    async getById(id: string): Promise<PatientOutcome> {
      const r = await apiClient.get(`/dermato/outcomes/${id}`);
      return unwrap<PatientOutcome>(r.data);
    },
    async update(id: string, data: UpdatePatientOutcomeDto): Promise<PatientOutcome> {
      const r = await apiClient.patch(`/dermato/outcomes/${id}`, data);
      return unwrap<PatientOutcome>(r.data);
    },
    async delete(id: string): Promise<void> {
      await apiClient.delete(`/dermato/outcomes/${id}`);
    },
    async list(params?: {
      page?: number;
      limit?: number;
      lesionId?: string;
      patientId?: string;
      status?: FinalOutcome;
    }): Promise<PaginatedResponse<PatientOutcome>> {
      const r = await apiClient.get('/dermato/outcomes', { params });
      return buildPaginated<PatientOutcome>(r.data, params);
    },
  },

  // Nomenclatură tumorală (§2.1.4) -----------------------------------------
  /**
   * Caută în nomenclatura tumorală. Căutarea este case- și diacritic-insenzibilă
   * pe server („bazocelular" găsește „Carcinom bazocelular").
   */
  async searchNomenclature(params?: {
    search?: string;
    layer?: TumorNomenclatureLayer;
    limit?: number;
  }): Promise<TumorNomenclatureSearchResult> {
    const r = await apiClient.get('/nomenclature', { params });
    return unwrap<TumorNomenclatureSearchResult>(r.data);
  },

  // Cronologie formular (§2.10.1–§2.10.3) ---------------------------------
  /** Evenimentele formularului, ordonate descrescător după `occurredAt`. */
  async getTimeline(
    formId: string,
    params?: { type?: TimelineEventType; page?: number; pageSize?: number },
  ): Promise<TimelineResponse> {
    const r = await apiClient.get(`/dermato-forms/${formId}/timeline`, { params });
    return unwrap<TimelineResponse>(r.data);
  },

  // Documente formular + import automat (§2.3.1–§2.3.4) ---------------------
  /** Lista documentelor încărcate pe formular (pentru importul automat). */
  async listDocuments(formId: string): Promise<DermatoFormDocument[]> {
    const r = await apiClient.get(`/dermato-forms/${formId}/documents`);
    const v = unwrap<DermatoFormDocument[] | { data?: DermatoFormDocument[] }>(r.data);
    if (Array.isArray(v)) return v;
    if (Array.isArray(v?.data)) return v.data;
    return [];
  },

  /**
   * §2.3.1/§2.3.2 — extrage datele din document și întoarce **propunerea**.
   * Nu scrie nimic în modelele clinice.
   */
  async getImportProposal(formId: string, documentId: string): Promise<ImportProposal> {
    const r = await apiClient.post(`/dermato-forms/${formId}/documents/${documentId}/import/proposal`);
    return unwrap<ImportProposal>(r.data);
  },

  /**
   * §2.3.2–§2.3.4 — scrie **doar câmpurile confirmate** de utilizator.
   * Câmpurile care nu pot fi scrise (ex. lipsește biopsia/HP/diagnosticul/planul)
   * sunt întorcute în `skipped` cu motiv în română.
   */
  async confirmImport(
    formId: string,
    documentId: string,
    body: { lesionId?: string; fields: Record<string, { value: unknown }> },
  ): Promise<ImportConfirmResult> {
    const r = await apiClient.post(
      `/dermato-forms/${formId}/documents/${documentId}/import/confirm`,
      body,
    );
    return unwrap<ImportConfirmResult>(r.data);
  },
};

export default dermatoApi;
